import { useEffect } from 'react';
import { articlesData, type ArticleData } from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';

function ComparisonTable({ rows }: { rows: ArticleData['sections'][0]['rows'] }) {
  if (!rows) return null;
  return (
    <div className="overflow-x-auto rounded-2xl border border-background-200/60">
      <table className="w-full text-left">
        <thead>
          <tr className="bg-background-100">
            <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">Платформа</th>
            <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">Цена</th>
            <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">Скорость</th>
            <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">Надёжность</th>
            <th className="px-5 py-4 text-foreground-700 font-semibold text-sm font-sans whitespace-nowrap">Для кого</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-background-200/60">
              <td className="px-5 py-4">
                <strong className="text-foreground-950 font-sans text-sm">{row.platform}</strong>
              </td>
              <td className="px-5 py-4">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">{row.price}</span>
              </td>
              <td className="px-5 py-4 text-foreground-600 text-sm font-sans">{row.speed}</td>
              <td className="px-5 py-4 text-foreground-600 text-sm font-sans">{row.trust}</td>
              <td className="px-5 py-4 text-foreground-600 text-sm font-sans">{row.bestFor}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BenefitsGrid({ benefits }: { benefits: ArticleData['sections'][0]['benefits'] }) {
  if (!benefits) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {benefits.map((item) => (
        <div key={item.title} className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
          <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
            <i className={`${item.icon} text-accent-600 text-lg`}></i>
          </div>
          <h3 className="font-sans text-foreground-950 font-semibold mb-2">{item.title}</h3>
          <p className="text-foreground-500 text-sm leading-relaxed font-sans">{item.desc}</p>
        </div>
      ))}
    </div>
  );
}

function StepsGuide({ steps }: { steps: ArticleData['sections'][0]['steps'] }) {
  if (!steps) return null;
  return (
    <div className="space-y-6">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-primary-600 font-bold text-sm font-sans">{i + 1}</span>
          </div>
          <div>
            <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-2">{step.title}</h3>
            <p className="text-foreground-600 leading-relaxed font-sans">{step.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArticleFAQ({ faqs }: { faqs: ArticleData['sections'][0]['faqs'] }) {
  if (!faqs) return null;
  return (
    <div className="space-y-4">
      {faqs.map((faq) => (
        <details key={faq.q} className="group bg-background-100 rounded-2xl border border-background-200/60">
          <summary className="px-6 py-5 cursor-pointer font-sans text-foreground-950 font-semibold flex items-center justify-between list-none">
            {faq.q}
            <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform w-5 h-5 flex items-center justify-center"></i>
          </summary>
          <div className="px-6 pb-5 text-foreground-500 leading-relaxed text-sm font-sans">{faq.a}</div>
        </details>
      ))}
    </div>
  );
}

interface Props {
  slug: string;
}

export default function ArticleLayout({ slug }: Props) {
  const article = articlesData.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  if (!article) {
    return (
      <main className="bg-background-50 min-h-screen flex items-center justify-center">
        <p className="text-foreground-500 font-sans">Статья не найдена</p>
      </main>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.imageUrl,
    datePublished: '2026-07-04',
    dateModified: '2026-07-04',
    author: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: 'https://yoybuyspreadsheet.lol/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'YOYBUY Spreadsheet',
      url: 'https://yoybuyspreadsheet.lol/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://yoybuyspreadsheet.lol/articles/${article.slug}`,
    },
  };

  return (
    <main className="bg-background-50 min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      {/* Navbar */}
      <nav className="bg-background-50 sticky top-0 z-40 border-b border-background-200/50">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2.5 no-underline">
            <div className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center">
              <span className="text-background-50 font-bold text-sm font-heading">Y</span>
            </div>
            <span className="font-bold text-foreground-950 text-sm whitespace-nowrap font-sans">yoybuy spreadsheet</span>
          </a>
          <a
            href="/"
            className="text-foreground-500 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            На главную
          </a>
        </div>
      </nav>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-6 py-12 md:py-20">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium font-sans whitespace-nowrap">
              {article.tag}
            </span>
            <span className="text-foreground-400 text-sm font-sans">Обновлено: Июль 2026</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 font-bold leading-tight mb-6">
            {article.title}
          </h1>
          {/* Hero Image */}
          <div className="w-full h-64 md:h-80 rounded-3xl overflow-hidden mb-8">
            <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" />
          </div>
          <p className="text-foreground-500 text-lg leading-relaxed font-sans">{article.intro}</p>
        </header>

        {/* Sections */}
        {article.sections.map((section, idx) => (
          <section key={idx} className="mb-12">
            {section.title && (
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mb-5">{section.title}</h2>
            )}
            {section.type === 'text' && section.paragraphs?.map((p, i) => (
              <p key={i} className="text-foreground-600 leading-relaxed mb-4 font-sans" dangerouslySetInnerHTML={{ __html: p }}></p>
            ))}
            {section.type === 'steps' && <StepsGuide steps={section.steps} />}
            {section.type === 'comparison' && <ComparisonTable rows={section.rows} />}
            {section.type === 'benefits' && <BenefitsGrid benefits={section.benefits} />}
            {section.type === 'faq' && <ArticleFAQ faqs={section.faqs} />}
          </section>
        ))}

        {/* CTA */}
        <section className="bg-foreground-950 rounded-3xl p-8 md:p-12 text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mb-4">
            {article.ctaText}
          </h2>
          <p className="text-foreground-300 mb-8 max-w-lg mx-auto font-sans">
            Присоединяйтесь к тысячам покупателей, которые уже экономят с YOYBUY Spreadsheet
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-10 py-4 bg-accent-500 text-background-50 rounded-full font-semibold text-base hover:bg-accent-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
          >
            Перейти к YOYBUY Spreadsheet
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </section>

        {/* Internal Links */}
        <nav className="border-t border-background-200/60 pt-8 mb-12">
          <h3 className="font-sans text-foreground-950 font-semibold text-lg mb-4">Полезные ссылки</h3>
          <div className="flex flex-wrap gap-3">
            <a href="/" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              Главная YOYBUY Spreadsheet
            </a>
            <a href="/shopping-guide" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
              <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
              Руководство по покупкам
            </a>
            <a href="/articles/yoybuy-spreadsheet-guide" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:text-foreground-950 text-sm font-medium font-sans no-underline transition-colors whitespace-nowrap">
              <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
              Полное руководство
            </a>
          </div>
        </nav>
      </article>

      <Footer />
    </main>
  );
}